import Button from '@mui/material/Button'; // Assuming Material UI is installed
import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const InstallPWAButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>(
    'other'
  );
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionMessage, setInstructionMessage] = useState<string>('');

  // Check if running in standalone mode or PWA is already installed & platform
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true
      ) {
        setIsAppInstalled(true);
      } else {
        console.log('[InstallPWAButton] App is not in standalone mode.');
      }

      const ua = window.navigator.userAgent.toLowerCase();
      const isIosDevice =
        /iphone|ipad|ipod/.test(ua) ||
        (navigator.platform === 'MacIntel' &&
          navigator.maxTouchPoints > 1 &&
          !(window as any).MSStream);

      if (isIosDevice) {
        console.log('[InstallPWAButton] Platform detected: iOS');
        setPlatform('ios');
      } else if (/android/.test(ua)) {
        console.log('[InstallPWAButton] Platform detected: Android');
        setPlatform('android');
      } else {
        console.log('[InstallPWAButton] Platform detected: Other');
        setPlatform('other');
      }
    }
  }, []);

  // Listener for 'beforeinstallprompt' event
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isAppInstalled) {
        return;
      }
      if (platform === 'ios') {
        return;
      }

      if (platform === 'android' || platform === 'other') {
        // Listen on Android and 'other' just in case
        const handler = (e: Event) => {
          e.preventDefault();
          setDeferredPrompt(e as BeforeInstallPromptEvent); // <<<< THE FIX
        };

        window.addEventListener('beforeinstallprompt', handler);
      }
    }
  }, [isAppInstalled, platform]);

  const handleInstallClick = async () => {
    setShowInstructions(true); // Always show the instruction panel on click, initially

    if (platform === 'ios') {
      setInstructionMessage(
        'To install on your iOS device:\n1. Tap the "Share" icon in your browser.\n2. Scroll down and tap "Add to Home Screen".'
      );
      return;
    }

    if (deferredPrompt && platform === 'android') {
      deferredPrompt.prompt();
      try {
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setIsAppInstalled(true);
          setShowInstructions(false);
        } else {
          setInstructionMessage(
            'Installation cancelled.\nTo install manually:\n1. Tap the menu (⋮) in your browser.\n2. Tap "Install app" or "Add to Home screen".'
          );
        }
      } catch (error) {
        setInstructionMessage(
          'If installation did not start:\n1. Tap the menu (⋮) in your browser.\n2. Tap "Install app" or "Add to Home screen".'
        );
      }
      setDeferredPrompt(null); // The prompt can only be used once
    } else if (platform === 'android' && !deferredPrompt) {
      setInstructionMessage(
        'To install this app:\n1. Tap the menu (⋮) in your browser.\n2. Look for "Install app" or "Add to Home Screen".'
      );
    } else if (platform === 'other') {
      // '[InstallPWAButton] Platform is "Other", showing generic instructions.'
      setInstructionMessage(
        'To add this app to your home screen, please look for an "Add to Home Screen" or "Install" option in your browser\'s menu.'
      );
    }
  };

  if (isAppInstalled) {
    return null;
  }

  const canAttemptInstall =
    platform === 'ios' ||
    platform === 'android' ||
    (platform === 'other' && deferredPrompt);

  return (
    <>
      <Button
        variant="contained"
        style={{ backgroundColor: '#E2002D', marginBottom: 12, color: 'white' }}
        onClick={handleInstallClick}
        disabled={!canAttemptInstall}
      >
        Add to Home Screen
      </Button>

      {showInstructions && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            background: '#f9f9f9',
            border: '1px solid #ccc',
            borderRadius: 4,
            maxWidth: 300,
            textAlign: 'left',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.9rem', whiteSpace: 'pre-line' }}>
            {instructionMessage}
          </p>
          <Button
            size="small"
            style={{ marginTop: 12, backgroundColor: '#bbb', color: '#fff' }}
            onClick={() => setShowInstructions(false)}
          >
            Got it
          </Button>
        </div>
      )}
    </>
  );
};

export default InstallPWAButton;
